import clsx from "clsx";
import React, { useContext } from "react";
import {
  Container,
  ContainerProps,
  Typography,
  Box,
  useTheme,
  Select,
  MenuItem,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Users } from "lucide-react";

import { ActivityResponseCard } from "../../components/activity/ActivityResponseCard";
import { LoadMoreButton } from "../button/LoadMoreButton";

import {
  NavigationItem,
  ActivityStatus,
  ActivityType,
  ActivitySort,
  defaultCoachActivityFilters,
  ActivitySortLocalStorageParameter,
  JourneyAnchors,
} from "../../constants";
import { PageSkeleton } from "../loading/PageSkeleton";
import {
  ActivityTypesFilter,
  ActivityTypesFilterProps,
} from "../filters/ActivityTypesFilter";
import { ActivitySortFilter } from "../filters/ActivitySortFilter";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";

import {
  COACH_ACTIVITY_ARCHIVED_ROUTE,
  COACH_ACTIVITY_INBOX_ROUTE,
} from "../../routes/routes";
import TrackInfoTool from "../tools/TrackInfoTool";
import useInfiniteScrollQuery from "../../hooks/useInfiniteScroll";
import ActivitiesService from "../../services/ActivitiesService";
import { maybePluralize } from "../../utils/text";
import { deepCompare } from "../editor/utils/common";
import { InfoBox } from "../info/InfoBox";
import { JourneyAvailabilityContext } from "../../contexts/JourneyAvailabilityContext";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
    },
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  leftPart: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexGrow: 1,
  },
  rightPart: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
      flexShrink: 0,
    },
  },
  iconButton: {
    width: 40,
    height: 40,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  iconActive: {
    borderColor: theme.palette.text.disabled,
  },
  clientFilter: {
    minWidth: 220,
    flexGrow: 1,
    maxWidth: 350,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      minWidth: 150,
    },
  },
  select: {
    backgroundColor: theme.palette.background.paper,
    minHeight: 40,
    height: 40,
    "& [role=combobox]": {
      color: theme.palette.text.secondary,
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
    },
  },
  inputActive: {
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  primaryText: {
    fontWeight: 500,
  },
  selectRenderValue: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  clientFilterIcon: {
    color: theme.palette.text.secondary,
  },
  none: {
    marginTop: theme.spacing(5),
    [theme.breakpoints.up("md")]: {
      marginTop: theme.spacing(10),
    },
  },
}));

export const coachActivityNavigation: NavigationItem[] = [
  {
    name: "Inbox",
    link: COACH_ACTIVITY_INBOX_ROUTE,
  },
  {
    name: <Box className={JourneyAnchors.ACTIVITIES_ARCHIVED}>Archived</Box>,
    link: COACH_ACTIVITY_ARCHIVED_ROUTE,
  },
];

export const getCoachActivityTypes = (
  filters: typeof defaultCoachActivityFilters,
) =>
  Object.entries(filters)
    .filter(([, enabled]) => enabled)
    .map(([type]) => type) as ActivityType[];

export interface CoachActivityProps extends Omit<ContainerProps, "children"> {
  status: ActivityStatus;
  clientIds?: number[];
  groupIds?: number[];
  enableArchive?: boolean;
  openClientsDialog?: () => void;
  clientFilterCount?: number;
}

const COACH_ACTIVITY_LIST_PAGE_SIZE = 10;
export const ACTIVITY_LIST_QUERY_KEY = "activity-list";

export function CoachActivity(props: CoachActivityProps) {
  const {
    className,
    status,
    clientIds = [],
    groupIds = [],
    enableArchive = false,
    openClientsDialog,
    clientFilterCount,
    ...other
  } = props;

  const s = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [visibleModalVideo, setVisibleModalVideo] = React.useState(false);
  const [videoUrl, setVideoUrl] = React.useState(null);

  const [sortedBy, setSortedBy] = useLocalStorage(
    ActivitySortLocalStorageParameter.SORT,
    ActivitySort.COMPLETED_AT_DESC,
  );

  const [filters, setFilters] = useLocalStorage<Record<ActivityType, boolean>>(
    ActivitySortLocalStorageParameter.FILTER,
    defaultCoachActivityFilters,
  );

  const activityTypes = React.useMemo(
    () => getCoachActivityTypes(filters),
    [filters],
  );
  const memoClientIds = React.useMemo(
    () => clientIds,
    [JSON.stringify(clientIds)],
  );
  const memoGroupIds = React.useMemo(
    () => groupIds,
    [JSON.stringify(groupIds)],
  );

  const {
    data: activitiesData,
    ref: activityElementRef,
    isLoading: isLoadingInitial,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteScrollQuery({
    queryKey: [
      ACTIVITY_LIST_QUERY_KEY,
      {
        status,
        orderBy: sortedBy,
        activityTypes,
        clientIds: memoClientIds,
        groupIds: memoGroupIds,
      },
    ],
    queryFn: ({ pageParam }) =>
      ActivitiesService.getActivities(
        pageParam as string | null,
        COACH_ACTIVITY_LIST_PAGE_SIZE,
        activityTypes,
        status === ActivityStatus.ARCHIVED,
        sortedBy,
        memoClientIds,
        memoGroupIds,
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor,
  });

  const activities = activitiesData?.pages.flatMap((page) => page.items) ?? [];

  const { setAvailability } = useContext(JourneyAvailabilityContext);

  React.useEffect(() => {
    const hasActivities = activities.length > 0;
    setAvailability({
      enabled: hasActivities,
      disabledReason: hasActivities
        ? undefined
        : "At least one activity is required to start the journey",
    });
    return () => setAvailability({ enabled: true });
  }, [activities.length, setAvailability]);

  const handleFiltersChange: ActivityTypesFilterProps["onChange"] =
    React.useCallback(
      (filters) => {
        setFilters(filters);
      },
      [setFilters],
    );

  const handleSortChange = React.useCallback(
    (newSort: ActivitySort) => {
      setSortedBy(newSort);
    },
    [setSortedBy],
  );

  const handleMoreClick = React.useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  }, [fetchNextPage, isFetchingNextPage, hasNextPage]);

  const handleOpenVideo = React.useCallback((url) => {
    setVisibleModalVideo(true);
    setVideoUrl(url);
  }, []);

  const handleCloseVideo = React.useCallback(() => {
    setVisibleModalVideo(false);
    setVideoUrl(null);
  }, []);

  const isLoading = isLoadingInitial || (isFetching && !isFetchingNextPage);

  const clientFilterLabel = React.useMemo(() => {
    if (clientFilterCount === 0) return "All clients";
    return [
      clientIds.length ? maybePluralize(clientIds.length, "client") : "",
      groupIds.length ? maybePluralize(groupIds.length, "group") : "",
    ]
      .filter(Boolean)
      .join(" & ");
  }, [clientFilterCount, clientIds.length, groupIds.length]);
  const isAnyFilterApplied = React.useMemo(() => {
    return !Object.values(filters).every(Boolean);
  }, [filters]);
  return (
    <>
      <Container className={clsx(s.root, className)} {...other}>
        <Box className={s.actions}>
          <Box className={s.leftPart}>
            {openClientsDialog && (
              <Select
                open={false}
                value=""
                displayEmpty
                variant="outlined"
                onClick={openClientsDialog}
                className={clsx(
                  s.select,
                  s.clientFilter,
                  clientFilterCount > 0 && s.inputActive,
                  JourneyAnchors.ACTIVITIES_FILTER,
                )}
                renderValue={() => (
                  <Box className={s.selectRenderValue}>
                    <Users size={20} className={s.clientFilterIcon} />
                    <Typography className={s.primaryText}>
                      {clientFilterLabel}
                    </Typography>
                  </Box>
                )}
              >
                <MenuItem value="">
                  <ListItemText
                    primary={clientFilterLabel}
                    classes={{ primary: s.primaryText }}
                  />
                </MenuItem>
              </Select>
            )}
            {!isMobile && (
              <ActivityTypesFilter
                active={isAnyFilterApplied}
                filters={filters}
                onChange={handleFiltersChange}
              />
            )}
          </Box>
          <Box className={s.rightPart}>
            {isMobile && (
              <ActivityTypesFilter
                active={isAnyFilterApplied}
                filters={filters}
                onChange={handleFiltersChange}
              />
            )}
            <ActivitySortFilter
              active={sortedBy !== ActivitySort.COMPLETED_AT_DESC}
              sortedBy={sortedBy}
              onChange={handleSortChange}
            />
          </Box>
        </Box>

        {isLoading ? (
          <PageSkeleton fullWidth />
        ) : activities.length ? (
          <>
            {activities.map((activity, index) => (
              <>
                <div ref={activityElementRef}></div>
                <Box
                  className={clsx({
                    [JourneyAnchors.FIRST_ACTIVITY]: index === 0,
                  })}
                >
                  <ActivityResponseCard
                    enableArchive={enableArchive}
                    activity={activity}
                    handleOpenVideo={handleOpenVideo}
                  />
                </Box>
              </>
            ))}

            {hasNextPage && !isLoading && (
              <LoadMoreButton
                onClick={handleMoreClick}
                loading={isFetchingNextPage}
              />
            )}
          </>
        ) : status === ActivityStatus.ACTIVE ? (
          <InfoBox
            className={s.none}
            title="No new activities"
            subtitle={`Don't worry, just wait for new completed activities from ${enableArchive ? "your clients" : "this client"}.`}
          />
        ) : (
          <InfoBox
            className={s.none}
            title="No archived activities"
            subtitle="You haven't archived any activities yet."
          />
        )}
      </Container>
      <ConfirmActionDialog
        title=""
        onCancel={handleCloseVideo}
        onConfirm={() => {}}
        open={visibleModalVideo}
        activity
        videoUrl={videoUrl}
      />
      <TrackInfoTool
        trackInfo={{
          name: clientIds
            ? "Coach - Client Activities"
            : status === ActivityStatus.ACTIVE
              ? "Coach - Activity"
              : "Coach - Archived Activity",
        }}
      />
    </>
  );
}
